import { bool, func, object, shape } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _get from 'lodash/get';

import { get } from 'ravenjs/utils/lodash';
import Typography from 'ravenjs/lib/Typography';

import { WarningIcon } from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';
import { MODALS } from 'constants/modals';
import MESSAGES from 'constants/messages';
import { selectors as authSelectors } from 'modules/auth';
import { actions as uiActions, selectors as uiSelectors } from 'modules/ui';
import { actions as bootstrapActions, selectors as bootstrapSelectors } from 'modules/bootstrap';
import { getDowntimeWarningPhase } from 'utils/date';

import Modal from '../Modal';
import DowntimeWithTimer from './DowntimeWithTimer';

const ModalStyled = styled.div`
    align-items: center;
    display: flex;
    justify-content: flex-start;
    padding: 1rem 2rem 0;
    > * {
        margin-right: 10px;
        &:last-child {
            margin-right: 0;
        }
    }
`;

const DescriptionStyled = styled.div`
    padding: 10px;
`;

const DowntimeWarningModal = props => {
    const { closeModal, downtimeInfo, fetchDowntimeInfo, openModal, modal, isLoggedIn } = props;
    const [phase, setPhase] = useState(null);
    const open = get(modal, 'open', false);

    const actions = [
        {
            text: MESSAGES.MODAL.CONFIRM.ACTIONS.OK,
            color: 'primary',
            onClick: () => {
                closeModal(MODALS.DOWNTIME_WARNING_MODAL);
                setPhase(null);
            },
            style: {
                minWidth: '240px',
                minHeight: '48px',
                fontSize: '1rem',
                fontWeight: 'normal',
                fontStyle: 'normal',
                color: DEFAULT_COLORS.BLACK_00,
            },
        },
    ];

    const checkWarningInterval = useCallback(async () => {
        const startTime = _get(downtimeInfo, 'downtime.start', null);
        const warningPhaseNow = getDowntimeWarningPhase(startTime);
        if (warningPhaseNow && phase !== warningPhaseNow) {
            const resp = await fetchDowntimeInfo();
            const latestStartTime = _get(resp, 'maintenance.downtime.start', null);
            if (latestStartTime === startTime) {
                openModal(MODALS.DOWNTIME_WARNING_MODAL, {
                    data: warningPhaseNow,
                });
                setPhase(warningPhaseNow);
            }
        }
    }, [downtimeInfo, fetchDowntimeInfo, openModal, phase]);

    useEffect(() => {
        const checkInterval =
            isLoggedIn &&
            setInterval(() => {
                checkWarningInterval();
            }, 60000);
        return () => clearInterval(checkInterval);
    }, [checkWarningInterval, isLoggedIn]);

    useEffect(() => {
        isLoggedIn && fetchDowntimeInfo();
    }, [fetchDowntimeInfo, isLoggedIn]);

    const getDescription = () => {
        return phase === 'TERMINAL_PHASE' ? (
            <DowntimeWithTimer />
        ) : (
            MESSAGES.DOWNTIME[phase].DESCRIPTION
        );
    };

    const renderContent = () => {
        return (
            phase && (
                <ModalStyled>
                    <WarningIcon style={{ width: 45, height: 'auto', color: DEFAULT_COLORS.RED }} />
                    <DescriptionStyled>
                        <Typography>{getDescription()}</Typography>
                    </DescriptionStyled>
                </ModalStyled>
            )
        );
    };

    return (
        <Modal
            id={MODALS.DOWNTIME_WARNING_MODAL}
            actions={actions}
            content={renderContent()}
            ModalProps={{
                style: {
                    maxWidth: '35rem',
                },
            }}
            modalHeaderProps={{
                paddingLeft: '2rem',
            }}
            modalFooterProps={{
                padding: '1rem 0 1.5rem 0',
            }}
            open={!!(open && phase)}
            title={phase && MESSAGES.DOWNTIME[phase].TITLE}
            backDropProps={{ blur: '4px' }}
        />
    );
};

DowntimeWarningModal.propTypes = {
    openModal: func.isRequired,
    closeModal: func.isRequired,
    downtimeInfo: object.isRequired,
    fetchDowntimeInfo: func.isRequired,
    isLoggedIn: bool.isRequired,
    modal: shape({
        open: bool,
    }).isRequired,
};

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
    openModal: uiActions.openModal,
    fetchDowntimeInfo: bootstrapActions.fetchDowntimeInfo,
};

const mapStateToProps = () =>
    createStructuredSelector({
        modal: uiSelectors.getModalById(MODALS.DOWNTIME_WARNING_MODAL),
        downtimeInfo: bootstrapSelectors.getDowntimeInfo,
        isLoggedIn: authSelectors.isUserLoggedIn,
    });

export { DowntimeWarningModal as DowntimeWarningModalUnwrapped };

export default connect(mapStateToProps, mapDispatchToProps)(DowntimeWarningModal);
